import { deleteDB, openDB } from "idb";
import type { DBSchema, IDBPDatabase } from "idb";

import type {
  ContentBundleEnvelope,
  EmergencyContactRecord,
  FamilyPlanRecord,
  MeetingPointRecord,
  PreparednessChecklistRecord,
  SupportedLocale,
} from "@safesphere/contracts";
import type { ReleaseIndexCandidate, ReleaseManifest } from "@safesphere/release";
import type { DisasterSession } from "../disaster/machine.js";
import type { EncryptedVaultRecord, VaultMetadata } from "../family/storageTypes.js";

export const offlineDatabaseName = "safesphere-offline";
const offlineDatabaseVersion = 4;

export interface StoredVerifiedBundle {
  readonly bundle: ContentBundleEnvelope;
  readonly verifiedAt: string;
}

interface BundlePointer {
  readonly key: string;
  readonly bundleId: string;
  readonly locale: SupportedLocale;
  readonly type: "active" | "known-good";
  readonly updatedAt: string;
}

interface PreferenceRecord {
  readonly key: string;
  readonly value: unknown;
}

interface DisasterSessionRecord {
  readonly key: string;
  readonly session: DisasterSession;
  readonly updatedAt: string;
}

export type UpdateStageStatus =
  "DISCOVERED" | "DOWNLOADING" | "DOWNLOADED" | "VERIFYING" | "VERIFIED" | "FAILED" | "ACTIVATED";

export interface StagedUpdate {
  readonly id: string;
  readonly locale: SupportedLocale;
  readonly status: UpdateStageStatus;
  readonly candidate: ReleaseIndexCandidate;
  readonly bundle?: ContentBundleEnvelope;
  readonly manifest?: ReleaseManifest;
  readonly failureReason?: string;
  readonly updatedAt: string;
}

export interface UpdateAuditEvent {
  readonly id: string;
  readonly occurredAt: string;
  readonly locale: SupportedLocale;
  readonly type:
    | "DISCOVERY_FAILED"
    | "UPDATE_DISCOVERED"
    | "DOWNLOAD_INTERRUPTED"
    | "DOWNLOAD_FAILED"
    | "VERIFICATION_FAILED"
    | "UPDATE_VERIFIED"
    | "ACTIVATED"
    | "ROLLBACK";
  readonly bundleId?: string;
  readonly detail: string;
}

export interface SavedPlacePlaceholder {
  readonly id: string;
  readonly label: string;
  readonly placeholderOnly: true;
}

export interface FamilyPlanPlaceholder {
  readonly id: string;
  readonly placeholderOnly: true;
}

interface SafeSphereOfflineSchema extends DBSchema {
  bundles: { key: string; value: StoredVerifiedBundle };
  pointers: { key: string; value: BundlePointer };
  preferences: { key: string; value: PreferenceRecord };
  savedPlaces: { key: string; value: SavedPlacePlaceholder };
  familyPlan: { key: string; value: FamilyPlanPlaceholder };
  disasterSessions: { key: string; value: DisasterSessionRecord };
  stagedUpdates: { key: string; value: StagedUpdate };
  updateAudit: { key: string; value: UpdateAuditEvent };
  familyPlans: { key: string; value: FamilyPlanRecord };
  familyContacts: { key: string; value: EmergencyContactRecord };
  familyMeetingPoints: { key: string; value: MeetingPointRecord };
  familyChecklists: { key: string; value: PreparednessChecklistRecord };
  vaultMetadata: { key: string; value: VaultMetadata };
  vaultRecords: { key: string; value: EncryptedVaultRecord };
}

let databasePromise: Promise<IDBPDatabase<SafeSphereOfflineSchema>> | undefined;

function pointerKey(locale: SupportedLocale, type: BundlePointer["type"]): string {
  return `${locale}:${type}`;
}

export function getOfflineDatabase(): Promise<IDBPDatabase<SafeSphereOfflineSchema>> {
  databasePromise ??= openDB<SafeSphereOfflineSchema>(offlineDatabaseName, offlineDatabaseVersion, {
    upgrade(database) {
      if (!database.objectStoreNames.contains("bundles")) database.createObjectStore("bundles");
      if (!database.objectStoreNames.contains("pointers")) database.createObjectStore("pointers");
      if (!database.objectStoreNames.contains("preferences"))
        database.createObjectStore("preferences");
      if (!database.objectStoreNames.contains("savedPlaces"))
        database.createObjectStore("savedPlaces");
      if (!database.objectStoreNames.contains("familyPlan"))
        database.createObjectStore("familyPlan");
      if (!database.objectStoreNames.contains("disasterSessions"))
        database.createObjectStore("disasterSessions");
      if (!database.objectStoreNames.contains("stagedUpdates"))
        database.createObjectStore("stagedUpdates");
      if (!database.objectStoreNames.contains("updateAudit"))
        database.createObjectStore("updateAudit");
      if (!database.objectStoreNames.contains("familyPlans"))
        database.createObjectStore("familyPlans");
      if (!database.objectStoreNames.contains("familyContacts"))
        database.createObjectStore("familyContacts");
      if (!database.objectStoreNames.contains("familyMeetingPoints"))
        database.createObjectStore("familyMeetingPoints");
      if (!database.objectStoreNames.contains("familyChecklists"))
        database.createObjectStore("familyChecklists");
      if (!database.objectStoreNames.contains("vaultMetadata"))
        database.createObjectStore("vaultMetadata");
      if (!database.objectStoreNames.contains("vaultRecords"))
        database.createObjectStore("vaultRecords");
    },
  });
  return databasePromise;
}

export async function putStagedUpdate(update: StagedUpdate): Promise<void> {
  await (await getOfflineDatabase()).put("stagedUpdates", update, update.id);
}

export async function getStagedUpdate(id: string): Promise<StagedUpdate | undefined> {
  return (await getOfflineDatabase()).get("stagedUpdates", id);
}

export async function listStagedUpdates(): Promise<readonly StagedUpdate[]> {
  return (await getOfflineDatabase()).getAll("stagedUpdates");
}

export async function appendUpdateAudit(event: UpdateAuditEvent): Promise<void> {
  await (await getOfflineDatabase()).put("updateAudit", event, event.id);
}

export async function listUpdateAudit(): Promise<readonly UpdateAuditEvent[]> {
  return (await getOfflineDatabase()).getAll("updateAudit");
}

/** Preferences hold small, non-sensitive local UI state only. */
export async function getLocalPreference(key: string): Promise<unknown> {
  return (await getOfflineDatabase()).get("preferences", key).then((record) => record?.value);
}

export async function setLocalPreference(key: string, value: unknown): Promise<void> {
  await (await getOfflineDatabase()).put("preferences", { key, value }, key);
}

export async function deleteLocalPreference(key: string): Promise<void> {
  await (await getOfflineDatabase()).delete("preferences", key);
}

export async function activateVerifiedBundle(record: StoredVerifiedBundle): Promise<void> {
  const database = await getOfflineDatabase();
  const locale = record.bundle.body.locale;
  const transaction = database.transaction(["bundles", "pointers"], "readwrite");
  const activeKey = pointerKey(locale, "active");
  const knownGoodKey = pointerKey(locale, "known-good");
  const previousActive = await transaction.objectStore("pointers").get(activeKey);
  const now = new Date().toISOString();

  await transaction.objectStore("bundles").put(record, record.bundle.body.bundleId);
  await transaction.objectStore("pointers").put(
    {
      key: knownGoodKey,
      bundleId: previousActive?.bundleId ?? record.bundle.body.bundleId,
      locale,
      type: "known-good",
      updatedAt: now,
    },
    knownGoodKey,
  );
  await transaction.objectStore("pointers").put(
    {
      key: activeKey,
      bundleId: record.bundle.body.bundleId,
      locale,
      type: "active",
      updatedAt: now,
    },
    activeKey,
  );
  await transaction.done;
}

export interface BundleStorageSnapshot {
  readonly activeBundle?: StoredVerifiedBundle;
  readonly activePointerExists: boolean;
  readonly knownGoodBundle?: StoredVerifiedBundle;
}

export async function getBundleStorageSnapshot(
  locale: SupportedLocale,
): Promise<BundleStorageSnapshot> {
  const database = await getOfflineDatabase();
  const transaction = database.transaction(["bundles", "pointers"]);
  const pointers = transaction.objectStore("pointers");
  const bundles = transaction.objectStore("bundles");
  const activePointer = await pointers.get(pointerKey(locale, "active"));
  const knownGoodPointer = await pointers.get(pointerKey(locale, "known-good"));
  const activeBundle =
    activePointer === undefined ? undefined : await bundles.get(activePointer.bundleId);
  const knownGoodBundle =
    knownGoodPointer === undefined ? undefined : await bundles.get(knownGoodPointer.bundleId);
  await transaction.done;

  return {
    ...(activeBundle === undefined ? {} : { activeBundle }),
    activePointerExists: activePointer !== undefined,
    ...(knownGoodBundle === undefined ? {} : { knownGoodBundle }),
  };
}

export async function restoreKnownGoodBundle(
  locale: SupportedLocale,
): Promise<StoredVerifiedBundle | undefined> {
  const database = await getOfflineDatabase();
  const transaction = database.transaction(["bundles", "pointers"], "readwrite");
  const pointers = transaction.objectStore("pointers");
  const knownGoodPointer = await pointers.get(pointerKey(locale, "known-good"));
  if (knownGoodPointer === undefined) {
    await transaction.done;
    return undefined;
  }

  const knownGoodBundle = await transaction.objectStore("bundles").get(knownGoodPointer.bundleId);
  if (knownGoodBundle === undefined) {
    await transaction.done;
    return undefined;
  }

  const activeKey = pointerKey(locale, "active");
  await pointers.put(
    {
      key: activeKey,
      bundleId: knownGoodPointer.bundleId,
      locale,
      type: "active",
      updatedAt: new Date().toISOString(),
    },
    activeKey,
  );
  await transaction.done;
  return knownGoodBundle;
}

export async function closeOfflineDatabase(): Promise<void> {
  if (databasePromise !== undefined) (await databasePromise).close();
  databasePromise = undefined;
}

export async function deleteOfflineDatabaseForTests(): Promise<void> {
  await closeOfflineDatabase();
  await deleteDB(offlineDatabaseName);
}
